const BLOCKED_PROTOCOLS = ['javascript:', 'vbscript:', 'data:'];
const ALLOWED_TARGETS = new Set(['_blank', '_self', '_parent', '_top']);

const getConfiguredOrigins = () => {
  const origins = new Set();

  if (typeof window !== 'undefined' && window.location?.origin) {
    origins.add(window.location.origin);
  }

  [
    process.env.REACT_APP_RELEASE_NOTE_MODULE_HOST,
    process.env.REACT_APP_HELP_MODULE_HOST,
    process.env.REACT_APP_INJECTABLE_MODULE_PROXYHOST,
    process.env.REACT_APP_SSO_SAML_URL,
    process.env.REACT_APP_SSO_URL,
  ]
    .filter(Boolean)
    .forEach((configuredUrl) => {
      try {
        origins.add(new URL(configuredUrl, window.location.origin).origin);
      } catch (error) {
        // Ignore invalid configured URLs.
      }
    });

  return origins;
};

const sanitizeWindowUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return null;
  }

  const lowerUrl = trimmedUrl.toLowerCase();
  if (BLOCKED_PROTOCOLS.some((protocol) => lowerUrl.startsWith(protocol))) {
    return null;
  }

  try {
    if (lowerUrl.startsWith('blob:')) {
      const parsedBlobUrl = new URL(trimmedUrl);
      if (parsedBlobUrl.protocol !== 'blob:') {
        return null;
      }
      return parsedBlobUrl.href;
    }

    const parsedUrl = new URL(trimmedUrl, window.location.origin);
    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      return null;
    }

    const isRelativePath =
      trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//');
    if (isRelativePath || getConfiguredOrigins().has(parsedUrl.origin)) {
      return parsedUrl.href;
    }
  } catch (error) {
    return null;
  }

  return null;
};

const sanitizeWindowTarget = (target) => {
  if (typeof target !== 'string') {
    return '_blank';
  }

  const trimmedTarget = target.trim();
  if (!trimmedTarget) {
    return '_blank';
  }

  if (ALLOWED_TARGETS.has(trimmedTarget)) {
    return trimmedTarget;
  }

  if (/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(trimmedTarget)) {
    return trimmedTarget;
  }

  return '_blank';
};

const openSanitizedUrlInNewContext = (sanitizedUrl, sanitizedTarget) => {
  const link = document.createElement('a');
  link.setAttribute('href', sanitizedUrl);
  link.setAttribute('target', sanitizedTarget);
  link.setAttribute('rel', 'noopener noreferrer');
  link.referrerPolicy = 'no-referrer';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  return link;
};

export const safeWindowOpen = (url, target = '_blank', features) => {
  const sanitizedUrl = sanitizeWindowUrl(url);
  if (!sanitizedUrl) {
    return null;
  }

  const sanitizedTarget = sanitizeWindowTarget(target);

  try {
    return openSanitizedUrlInNewContext(sanitizedUrl, sanitizedTarget);
  } catch (error) {
    return null;
  }
};

/**
 * Navigate the current window only after protocol/origin allowlist checks (CWE-601).
 * Uses location.assign with a parsed href so the redirect sink never receives raw input.
 */
export const safeLocationRedirect = (url) => {
  const sanitizedUrl = sanitizeWindowUrl(url);
  if (!sanitizedUrl) {
    return false;
  }

  window.location.assign(sanitizedUrl);
  return true;
};

export const openReleaseNotesUrl = () => {
  const releaseNotesUrl = (
    process.env.REACT_APP_RELEASE_NOTE_MODULE_HOST || ''
  ).trim();

  return safeWindowOpen(releaseNotesUrl, '_blank');
};

export const openHelpModuleUrl = (path = 'Default.htm', hashId = '') => {
  const helpHost = (process.env.REACT_APP_HELP_MODULE_HOST || '').trim();
  if (!helpHost) {
    return null;
  }

  const safePath =
    String(path).replace(/[^a-zA-Z0-9._-]/g, '') || 'Default.htm';
  const helpUrl = new URL(safePath, helpHost);

  if (hashId) {
    const safeHashId = String(hashId).replace(/[^a-zA-Z0-9_-]/g, '');
    if (safeHashId) {
      helpUrl.hash = `cshid=${safeHashId}`;
    }
  }

  return safeWindowOpen(helpUrl.toString(), '_blank');
};
